import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Flights from "@/pages/Flights";
import Agencies from "@/pages/Agencies";
import Services from "@/pages/Services";
import Agents from "@/pages/Agents";
import Reports from "@/pages/Reports";
import Invoices from "@/pages/Invoices";
import PayInvoice from "@/pages/PayInvoice";
import AgencyStatements from "@/pages/AgencyStatements";
import Planning from "@/pages/Planning";
import CompanySettings from "@/pages/CompanySettings";
import AdminUsers from "@/pages/AdminUsers";
import Permissions from "@/pages/Permissions";
import Airports from "@/pages/Airports";
import Statistics from "@/pages/Statistics";
import LoginHistory from "@/pages/LoginHistory";
import PendingTasks from "@/pages/PendingTasks";
import UserManual from "@/pages/UserManual";
import EmailAutomation from "@/pages/EmailAutomation";
import EmailTemplates from "@/pages/EmailTemplates";
import EmailLogs from "@/pages/EmailLogs";
import PopupsNotices from "@/pages/PopupsNotices";
import AirlinesData from "@/pages/AirlinesData";
import BannedDomains from "@/pages/BannedDomains";
import AgencyUsers from "@/pages/AgencyUsers";
import Backups from "@/pages/Backups";
import Layout from "@/components/Layout";
import GDPRPopup from "@/components/GDPRPopup";
import InactivityWrapper from "@/components/InactivityWrapper";
import { Toaster } from "@/components/ui/sonner";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGDPRPopup, setShowGDPRPopup] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  useEffect(() => {
    if (process.env.REACT_APP_AUTO_ACCEPT_GDPR === 'true') {
      localStorage.setItem('gdpr_consented', 'true');
    }
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    // Check if user has already accepted GDPR consent
    const hasGDPRConsent = localStorage.getItem('gdpr_consented') === 'true';
    
    if (hasGDPRConsent) {
      // User already accepted GDPR, proceed directly with login
      setUser(userData);
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      // Show GDPR popup for first-time consent
      setPendingLoginData({ userData, token });
      setShowGDPRPopup(true);
    }
  };

  const handleGDPRAccept = () => {
    // User accepted GDPR, save consent and proceed with login
    if (pendingLoginData) {
      // Persist GDPR consent in localStorage
      localStorage.setItem('gdpr_consented', 'true');
      
      setUser(pendingLoginData.userData);
      localStorage.setItem('token', pendingLoginData.token);
      localStorage.setItem('user', JSON.stringify(pendingLoginData.userData));
      setShowGDPRPopup(false);
      setPendingLoginData(null);
    }
  };

  const handleGDPRDecline = () => {
    // User declined GDPR, clear pending data and stay on login
    setShowGDPRPopup(false);
    setPendingLoginData(null);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear session storage for popup tracking
    sessionStorage.removeItem('sessionReadPopups');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen" style={{ background: '#0A1128', color: '#FFFFFF' }}>Loading...</div>;
  }

  // Route protection wrapper - redirects staff to dashboard if accessing restricted pages
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (allowedRoles && !allowedRoles.includes(user?.role)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <InactivityWrapper enabled={!!user} onLogout={handleLogout}>
          <Routes>
            <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
          
            <Route path="/" element={user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}>
            <Route index element={<Dashboard />} />
            <Route path="planning" element={<Planning />} />
            <Route path="flights" element={<ProtectedRoute allowedRoles={['admin', 'operations', 'accounting', 'agency', 'staff']}><Flights /></ProtectedRoute>} />
            <Route path="agencies" element={<ProtectedRoute allowedRoles={['admin', 'operations', 'accounting', 'agency']}><Agencies /></ProtectedRoute>} />
            <Route path="services" element={<ProtectedRoute allowedRoles={['admin', 'operations']}><Services /></ProtectedRoute>} />
            <Route path="airports" element={<ProtectedRoute allowedRoles={['admin']}><Airports /></ProtectedRoute>} />
            <Route path="agents" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><Agents /></ProtectedRoute>} />
            <Route path="reports" element={<ProtectedRoute allowedRoles={['admin', 'operations', 'agency', 'staff']}><Reports /></ProtectedRoute>} />
            <Route path="invoices" element={<ProtectedRoute allowedRoles={['admin', 'accounting']}><Invoices /></ProtectedRoute>} />
            <Route path="pay-invoice" element={<ProtectedRoute allowedRoles={['admin', 'accounting', 'agency']}><PayInvoice /></ProtectedRoute>} />
            <Route path="agency-statements" element={<ProtectedRoute allowedRoles={['admin', 'accounting', 'agency']}><AgencyStatements /></ProtectedRoute>} />
            <Route path="statistics" element={<ProtectedRoute allowedRoles={['admin', 'accounting', 'agency']}><Statistics /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute allowedRoles={['admin']}><CompanySettings /></ProtectedRoute>} />
            <Route path="login-history" element={<ProtectedRoute allowedRoles={['admin', 'agency']}><LoginHistory /></ProtectedRoute>} />
            <Route path="pending-tasks" element={<ProtectedRoute allowedRoles={['admin']}><PendingTasks /></ProtectedRoute>} />
            <Route path="admin-users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />
            <Route path="permissions" element={<ProtectedRoute allowedRoles={['admin']}><Permissions /></ProtectedRoute>} />
            <Route path="user-manual" element={<ProtectedRoute allowedRoles={['admin', 'operations', 'accounting', 'agency', 'staff']}><UserManual /></ProtectedRoute>} />
            <Route path="email-automation" element={<ProtectedRoute allowedRoles={['admin']}><EmailAutomation /></ProtectedRoute>} />
            <Route path="email-templates" element={<ProtectedRoute allowedRoles={['admin']}><EmailTemplates /></ProtectedRoute>} />
            <Route path="email-logs" element={<ProtectedRoute allowedRoles={['admin']}><EmailLogs /></ProtectedRoute>} />
            <Route path="popups-notices" element={<ProtectedRoute allowedRoles={['admin']}><PopupsNotices /></ProtectedRoute>} />
            <Route path="airlines-data" element={<ProtectedRoute allowedRoles={['admin']}><AirlinesData /></ProtectedRoute>} />
            <Route path="banned-domains" element={<ProtectedRoute allowedRoles={['admin']}><BannedDomains /></ProtectedRoute>} />
            <Route path="agency-users" element={<ProtectedRoute allowedRoles={['agency']}><AgencyUsers /></ProtectedRoute>} />
            <Route path="backups" element={<ProtectedRoute allowedRoles={['admin']}><Backups /></ProtectedRoute>} />
          </Route>
          </Routes>
        </InactivityWrapper>
        <Toaster position="top-right" richColors />
        
        {/* GDPR Compliance Popup - Shows after login */}
        {showGDPRPopup && (
          <GDPRPopup 
            onAccept={handleGDPRAccept}
            onDecline={handleGDPRDecline}
          />
        )}
      </BrowserRouter>
    </div>
  );
}

export default App;
